import { ChangeDetectionStrategy, Component, computed, forwardRef, input, model, output, signal } from '@angular/core';
import { NG_VALUE_ACCESSOR, type ControlValueAccessor } from '@angular/forms';

import type { EsnSearchFieldSize } from '../../../shared/types/forms';
import { IconComponent } from '../../atoms/icon/icon.component';

/**
 * The toolbar search box: the placeholder names it, Enter runs the search. The compact size
 * is for panels, with the magnifier leading instead of trailing.
 */
@Component({
  selector: 'esn-search-field',
  imports: [IconComponent],
  templateUrl: './search-field.component.html',
  styleUrl: './search-field.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => SearchFieldComponent), multi: true }],
  host: { '[class]': '"esn-search-field-host--" + size()', '[class.esn-search-field-host--full]': 'fullWidth()' },
})
export class SearchFieldComponent implements ControlValueAccessor {
  readonly value = model('');
  readonly size = input<EsnSearchFieldSize>('default');
  readonly placeholder = input('');

  /**
   * Accessible name; defaults to the placeholder, since the field has no visible label.
   */
  readonly ariaLabel = input<string | null>(null);
  readonly disabled = input(false);

  /**
   * Stretch to the container instead of the Figma 640/320px width (e.g. inside a panel).
   */
  readonly fullWidth = input(false);

  readonly search = output<string>();

  private readonly formDisabled = signal(false);
  protected readonly isDisabled = computed(() => this.disabled() || this.formDisabled());
  protected readonly accessibleName = computed(() => this.ariaLabel() ?? this.placeholder());

  private onChange: (value: string) => void = () => undefined;
  private onTouched: () => void = () => undefined;

  protected handleInput(event: Event): void {
    const next = (event.target as HTMLInputElement).value;
    this.value.set(next);
    this.onChange(next);
  }

  protected handleEnter(): void {
    this.search.emit(this.value());
  }

  protected handleBlur(): void {
    this.onTouched();
  }

  writeValue(value: string | null): void {
    this.value.set(value ?? '');
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(disabled: boolean): void {
    this.formDisabled.set(disabled);
  }
}
